import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import NoiRockLogo from '../../../components/NoiRockLogo';

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].charAt(0).toUpperCase();
};

const AdminMobileHeader = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      setEmail(user.email || '');
      const { data } = await supabase
        .from('profiles')
        .select('full_name, role')
        .eq('id', user.id)
        .single();
      if (!cancelled) setProfile(data);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  return (
    <header
      className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 border-b border-border bg-card"
      style={{
        paddingTop: 'max(12px, env(safe-area-inset-top))',
        paddingBottom: 12,
        minHeight: 'calc(56px + env(safe-area-inset-top))',
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        {showBack ? (
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('nav.back', 'Back')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <NoiRockLogo size={28} />
        )}
        {title && (
          <span className="font-heading font-bold text-foreground text-base truncate">
            {title}
          </span>
        )}
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground text-sm font-bold font-heading hover:ring-2 hover:ring-primary/40 transition-all"
        >
          {initialsOf(profile?.full_name)}
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 rounded-xl bg-card border border-border shadow-elevation-3 overflow-hidden min-w-[220px] z-50">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-heading text-sm font-semibold text-foreground truncate">
                {profile?.full_name || 'Admin'}
              </p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{email}</p>
            </div>
            <div className="py-1">
              <button
                onClick={() => { setOpen(false); navigate('/admin/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M2 14c0-2.761 2.686-5 6-5s6 2.239 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                {t('nav.profile', 'Profile')}
              </button>
              <button
                onClick={() => { setOpen(false); navigate('/admin/notifications'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2a4 4 0 00-4 4v2.5L2.5 11h11L12 8.5V6a4 4 0 00-4-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                  <path d="M6.5 12.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
                {t('nav.notifications', 'Notifications')}
              </button>
              <div className="border-t border-border my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-error hover:bg-error/10 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('nav.logout', 'Logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default AdminMobileHeader;
